import { useTranslation } from 'react-i18next';
import {
  ControlledCheckboxAutoComplete,
  ControlledDatePicker
} from 'components/shared/ControlledFields';
import { useFormContext } from 'react-hook-form';
import { StatusType } from '../../type';
import ControlledMultiSelect from 'components/shared/ControlledFields/ControlledMultiSelect';
import {
  useGetPlanUnitsOptions,
  useGetProjectOptions
} from '../hooks/useGetOptions';
import { toOptions } from 'utils/toOptions';
import Skeleton from '@mui/material/Skeleton';
import { isEmpty, isNil } from 'ramda';
import useSelectedDateRange from '../../../../../roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useEffect } from 'react';

const ProjectPlanningSection = () => {
  const { t } = useTranslation('common');
  const { control, watch, setValue } = useFormContext();

  const { startDate, endDate } = useSelectedDateRange();

  // This useEffect responsible to set default values for the form
  useEffect(() => {
    setValue('valid_from', startDate);
    setValue('valid_to', endDate);
    setValue('valid_to', endDate);
    setValue('br_id_list', []);
    setValue('project_entity_id_list', []);
    setValue('status', []);
  }, []);

  const { data: planUnits, isFetching: isFetchingPlanUnits } =
    useGetPlanUnitsOptions(
      watch('valid_from') ?? new Date(),
      watch('valid_to') ?? new Date()
    );
  const { data: projects, isFetching: isFetchingProjects } =
    useGetProjectOptions();

  return !isFetchingPlanUnits && !isFetchingProjects ? (
    <>
      <ControlledDatePicker
        required
        fullWidth
        variant="outlined"
        control={control}
        name={'valid_from'}
        label={t('validFrom')}
      />
      <ControlledDatePicker
        required
        fullWidth
        variant="outlined"
        control={control}
        name={'valid_to'}
        label={t('validTo')}
      />
      <MultiEditFields projects={projects} planUnits={planUnits} />
    </>
  ) : (
    <Skeleton />
  );
};

const MultiEditFields = ({ planUnits, projects }) => {
  const { t } = useTranslation('common');
  const methods = useFormContext();
  const { watch } = methods;
  const watchProjectIds = watch('project_entity_id_list');
  const watchBussinessIds = watch('br_id_list');

  const onRemoveElement = (id: number | string) => {
    methods.setValue(
      'br_id_list',
      methods.watch('br_id_list').filter((sid: number) => sid !== id),
      { shouldDirty: true }
    );
  };

  const onRemoveElementProjects = (id: number | string) => {
    methods.setValue(
      'project_entity_id_list',
      methods
        .watch('project_entity_id_list')
        .filter((sid: number) => sid !== id),
      { shouldDirty: true }
    );
  };

  return (
    <>
      <ControlledCheckboxAutoComplete
        fullWidth
        name="br_id_list"
        label={t('planUnits')}
        options={toOptions(planUnits ?? [], 'id', 'name')}
        control={methods.control}
        idProperty="value"
        labelProperty="label"
        allowItemRemoval
        disabled={!isNil(watchProjectIds) && !isEmpty(watchProjectIds)}
        onRemoveElement={onRemoveElement}
      />
      <ControlledCheckboxAutoComplete
        fullWidth
        name="project_entity_id_list"
        label={t('projects')}
        options={toOptions(projects ?? [], 'entityId', 'name')}
        control={methods.control}
        idProperty="value"
        labelProperty="label"
        allowItemRemoval
        disabled={!isNil(watchBussinessIds) && !isEmpty(watchBussinessIds)}
        onRemoveElement={onRemoveElementProjects}
      />
      <ControlledMultiSelect
        fullWidth
        name="statuses"
        label={t('status')}
        variant="outlined"
        options={[
          {
            value: StatusType.PLANNED,
            label: t('generatedReport.statuses.PLANNED')
          },
          {
            value: StatusType.APPROVED,
            label: t('generatedReport.statuses.APPROVED')
          },
          {
            value: StatusType.SUBMITTED,
            label: t('generatedReport.statuses.SUBMITTED')
          }
        ]}
      />
    </>
  );
};

export default ProjectPlanningSection;
